import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
} from "@tiny/components";
import Copy from "@tiny/icons/generated/Copy";
import DarkTooltip from "@tiny/components/src/DarkTooltip/DarkTooltip";
import { useGetRetentionIntegrationQuery } from "@tiny/api";
import { makeStyles } from "@tiny/components";

const useStyles = makeStyles((theme) => ({
  tabsBar: {
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    padding: theme.spacing(0, 6),
    marginTop: theme.spacing(4),
  },
  tabContent: {
    padding: theme.spacing(5, 0),
  },
  scriptBox: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 8,
    padding: "12px 14px",
    maxHeight: 110,
    overflowY: "auto",
    fontFamily:
      "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
    fontSize: 12,
    lineHeight: 1.8,
    color: theme.palette.grey[800],
    wordBreak: "break-all",
    whiteSpace: "pre-wrap",
  },
  stepList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  stepItem: {
    display: "flex",
    alignItems: "baseline",
    gap: 12,
    padding: "10px 0",
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    "&:last-child": { borderBottom: "none" },
  },
  stepNumber: {
    width: 22,
    height: 22,
    minWidth: 22,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 700,
    flexShrink: 0,
    marginTop: 2,
  },
}));

const CUSTOM_STEPS = [
  "Copy the script using the button above",
  "Paste it just before the closing </head> tag on every page",
  "Anonymous visitors will now be identified and tracked automatically",
];

const GTM_STEPS = [
  "Copy the script using the button above",
  "In GTM create a new Custom HTML tag and paste the script",
  "Submit and publish your GTM container",
];

interface ManageRetentionDialogProps {
  open: boolean;
  onClose: () => void;
  productId: string;
}

function ManageRetentionDialog(props: ManageRetentionDialogProps) {
  const { open, onClose } = props;
  const classes = useStyles();

  const [installTab, setInstallTab] = React.useState(0);
  const [showCopiedTooltip, setShowCopiedTooltip] = React.useState(false);

  const { data: integration, isLoading } = useGetRetentionIntegrationQuery({
    enabled: open,
  });

  React.useEffect(() => {
    if (!open) {
      setInstallTab(0);
    }
  }, [open]);

  const script = integration?.retentionScript ?? "";

  const handleCopy = React.useCallback(() => {
    if (script) {
      navigator.clipboard
        .writeText(script)
        .then(() => setShowCopiedTooltip(true));
    }
  }, [script]);

  const handleCloseTooltip = React.useCallback(
    () => setShowCopiedTooltip(false),
    []
  );

  const steps = installTab === 0 ? CUSTOM_STEPS : GTM_STEPS;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Retention.com Integration</DialogTitle>

      <DialogContent style={{ paddingTop: 0, paddingBottom: 0 }}>
        <Box className={classes.tabContent}>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={220}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box mb={3}>
                <Typography variant="body2" color="grey" tone={600}>
                  Install the tracking script on your website to start
                  identifying anonymous visitors.
                </Typography>
              </Box>

              <Box
                mb={1.5}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  variant="label1"
                  color="grey"
                  tone={900}
                  fontWeight="bold"
                >
                  Your Tracking Script
                </Typography>
                <DarkTooltip
                  title="Copied!"
                  open={showCopiedTooltip}
                  placement="top"
                  onClose={handleCloseTooltip}
                >
                  <Button
                    color="primary"
                    endIcon={<Copy />}
                    noPadding
                    variant="text"
                    onClick={handleCopy}
                    disabled={!script}
                  >
                    Copy script
                  </Button>
                </DarkTooltip>
              </Box>
              <Box mb={5}>
                <div className={classes.scriptBox}>
                  {script ||
                    "(Script will appear here once integration is active)"}
                </div>
              </Box>

              <Box
                mb={1.5}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  variant="h3"
                  color="grey"
                  tone={900}
                  fontWeight="bold"
                >
                  Installation
                </Typography>
                <Tabs
                  value={installTab}
                  onChange={(val: number) => setInstallTab(val)}
                >
                  <Tab label="Custom Website" value={0} />
                  <Tab label="Google Tag Manager" value={1} />
                </Tabs>
              </Box>

              <ol className={classes.stepList}>
                {steps.map((text, index) => (
                  <li key={index} className={classes.stepItem}>
                    <div className={classes.stepNumber}>{index + 1}</div>
                    <Typography variant="body2" color="grey" tone={700}>
                      {text}
                    </Typography>
                  </li>
                ))}
              </ol>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default ManageRetentionDialog;
